import { useMemo, useRef, useState } from 'react'
import { DataGrid, type GridColumn } from '@/components/blocks/data-grid'
import { useTableState } from '@/components/blocks/data-table'
import { applyTableState } from '@/components/blocks/data-table'
import { router, usePage } from '@inertiajs/react'
import { GameConfigShell } from '@/components/config/GameConfigShell'
import { Download, Upload } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ImportCardsDialog, type ImportedCard } from '@/components/config/ImportCardsDialog'
import { useToast } from '@/components/ui/Toast'

/* Exactly the columns CardsController hands over — the real table, not a
 * fixture shaped like one. */
export interface ConfigCard {
  id: string
  cr_name: string
  name: string | null
  rarity: string
  card_type: string
  cost: number
  arena: number
  count: number
  target_air: boolean
  target_ground: boolean
  transport: string | null
  speed: string | null
  hit_speed: string | null
  first_hit_speed: string | null
  range_tiles: string | null
  focus: string
  form_size: string | null
}

interface CardOptions {
  rarities: string[]
  card_types: string[]
  transports: string[]
  speeds: string[]
  focuses: string[]
  form_sizes: string[]
}

interface GameConfigCardsProps {
  cards: ConfigCard[]
  options: CardOptions
  can_edit: boolean
}

const RARITY_CHIP: Record<string, string> = {
  common: 'bg-bg-subtle text-fg-muted',
  rare: 'bg-accent-surface text-accent',
  epic: 'bg-warn-surface text-warn',
}

const BOOL_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

/* A boolean column has no grid type of its own; a two-option select is the
 * honest representation and round-trips as a string like every other cell. */
const yesNo = (value: boolean) => (value ? 'true' : 'false')

/** RFC 4180 enough: quote everything, double the quotes inside. */
function toCsv(rows: ConfigCard[], fields: (keyof ConfigCard)[]): string {
  const cell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`
  return [fields.join(','), ...rows.map((row) => fields.map((f) => cell(row[f])).join(','))].join(
    '\n',
  )
}

export default function GameConfigCards({ cards, options, can_edit }: GameConfigCardsProps) {
  const { toast } = useToast()
  const { state, setState } = useTableState({ pageSize: 50, sortKey: 'arena', sortDir: 'asc' })
  const [rows, setRows] = useState(cards)
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const ordinals = useRef(new Map<string, number>())
  const [importOpen, setImportOpen] = useState(false)

  /* Rails answers a failed save with `<row id>.<field>` keys; the grid wants
     them nested so it can mark one cell. */
  const flatErrors = usePage().props.errors as Record<string, string> | undefined
  const cellErrors = useMemo(() => {
    const nested: Record<string, Record<string, string>> = {}
    for (const [key, message] of Object.entries(flatErrors ?? {})) {
      const split = key.lastIndexOf('.')
      if (split === -1) continue
      const rowId = key.slice(0, split)
      const field = key.slice(split + 1)
      ;(nested[rowId] ??= {})[field] = message
    }
    return nested
  }, [flatErrors])
  const nextTempId = useRef(0)

  const columns: GridColumn<ConfigCard>[] = useMemo(
    () => [
      {
        key: 'stt',
        header: 'STT',
        width: '60px',
        type: 'read',
        /* Position in the view, not a stored field — it renumbers when you
           sort, which is the only reading of a row counter that stays true. */
        render: (row) => String((ordinals.current.get(row.id) ?? 0) + 1),
      },
      { key: 'name', header: 'Display name', width: 'minmax(170px,1.2fr)', type: 'text', sortable: true },
      { key: 'cr_name', header: 'CR name', width: 'minmax(150px,1fr)', type: 'text', sortable: true },
      {
        key: 'rarity',
        header: 'Rarity',
        width: '140px',
        type: 'select',
        sortable: true,
        options: options.rarities.map((r) => ({ value: r, label: r, chipClassName: RARITY_CHIP[r] })),
      },
      {
        key: 'card_type',
        header: 'Type',
        width: '140px',
        type: 'select',
        sortable: true,
        options: options.card_types.map((t) => ({ value: t, label: t })),
      },
      { key: 'cost', header: 'Elixir', width: '90px', type: 'number', align: 'end', sortable: true },
      { key: 'arena', header: 'Arena', width: '90px', type: 'number', align: 'end', sortable: true },
      { key: 'count', header: 'Count', width: '90px', type: 'number', align: 'end', sortable: true },
      {
        key: 'target_air',
        header: 'Targets air',
        width: '120px',
        type: 'select',
        sortable: true,
        value: (row) => yesNo(row.target_air),
        options: BOOL_OPTIONS,
      },
      {
        key: 'target_ground',
        header: 'Targets ground',
        width: '140px',
        type: 'select',
        sortable: true,
        value: (row) => yesNo(row.target_ground),
        options: BOOL_OPTIONS,
      },
      {
        key: 'transport',
        header: 'Transport',
        width: '130px',
        type: 'select',
        sortable: true,
        options: [{ value: '', label: '—' }, ...options.transports.map((t) => ({ value: t, label: t }))],
      },
      {
        key: 'speed',
        header: 'Speed',
        width: '170px',
        type: 'select',
        sortable: true,
        options: [{ value: '', label: '—' }, ...options.speeds.map((t) => ({ value: t, label: t }))],
      },
      {
        key: 'focus',
        header: 'Focus',
        width: '120px',
        type: 'select',
        sortable: true,
        options: options.focuses.map((f) => ({ value: f, label: f })),
      },
      {
        key: 'form_size',
        header: 'Form size',
        width: '130px',
        type: 'select',
        sortable: true,
        options: [{ value: '', label: '—' }, ...options.form_sizes.map((f) => ({ value: f, label: f }))],
      },
      { key: 'hit_speed', header: 'Hit speed', width: '110px', type: 'text' },
      { key: 'first_hit_speed', header: 'First hit', width: '110px', type: 'text' },
      { key: 'range_tiles', header: 'Range', width: '100px', type: 'text' },
    ],
    [options],
  )

  /* Unsaved rows stay out of the sort and sit at the end. Sorting a row the
     moment it appears drops it into the middle of the table, which is not where
     anyone who just pressed "New card" is looking. */
  const view = useMemo(() => {
    const pending = rows.filter((row) => row.id.startsWith('new:'))
    const saved = rows.filter((row) => !row.id.startsWith('new:'))
    const sorted = applyTableState(saved, columns, state)
    return { rows: [...sorted.rows, ...pending], total: sorted.total + pending.length }
  }, [rows, columns, state])

  /* Filled before <DataGrid> renders its cells, so the STT column can read the
     row's place in the current view without the columns memo depending on it. */
  ordinals.current = new Map(view.rows.map((row, index) => [row.id, index]))

  const handleCellChange = (row: ConfigCard, key: string, value: string) => {
    setRows((previous) =>
      previous.map((entry) => {
        if (entry.id !== row.id) return entry
        if (key === 'target_air' || key === 'target_ground') {
          return { ...entry, [key]: value === 'true' }
        }
        if (key === 'cost' || key === 'arena' || key === 'count') {
          return { ...entry, [key]: Number(value) || 0 }
        }
        return { ...entry, [key]: value === '' ? null : value }
      }),
    )
    setDirty((previous) => new Set(previous).add(row.id))
  }

  /* A new row is a row, not a detour to a form: the grid appends it, scrolls it
     into view and focuses the first cell. It carries a `new:` id so the save
     endpoint knows to create rather than update, and the column defaults match
     the table's own so a half-filled row still validates. */
  const handleAddRow = () => {
    const id = `new:${nextTempId.current++}`
    setRows((previous) => [
      ...previous,
      {
        id,
        cr_name: '',
        name: '',
        rarity: options.rarities[0] ?? 'common',
        card_type: options.card_types[0] ?? 'troop',
        cost: 0,
        arena: 0,
        count: 1,
        target_air: false,
        target_ground: true,
        transport: null,
        speed: null,
        hit_speed: null,
        first_hit_speed: null,
        range_tiles: null,
        focus: options.focuses[0] ?? 'units',
        form_size: null,
      },
    ])
    setDirty((previous) => new Set(previous).add(id))
  }

  const handleRevert = () => {
    setRows(cards)
    setDirty(new Set())
  }

  const handleSave = () => {
    const edited = rows.filter((row) => dirty.has(row.id))
    setSaving(true)

    /* One request to the section's own endpoint. Looping CardsController#update
       walked us off to /cards, because that action redirects to the index. */
    router.patch(
      '/game-config/cards',
      { cards: edited } as never,
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setDirty(new Set())
          toast(`Saved ${edited.length} card${edited.length === 1 ? '' : 's'}`, 'success')
        },
        onError: () => toast('Nothing was saved — check the highlighted cells.', 'danger'),
        onFinish: () => setSaving(false),
      },
    )
  }

  /* Real, not a placeholder: the rows are already here, so exporting them is a
     blob and a click. Import needs a parser and an endpoint, so it says so
     rather than pretending. */
  const handleExport = () => {
    const fields = columns
      .map((column) => column.key)
      .filter((key): key is keyof ConfigCard => key !== 'stt')
    const blob = new Blob([toCsv(view.rows, fields)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'cards.csv'
    anchor.click()
    URL.revokeObjectURL(url)
    toast(`Exported ${view.rows.length} cards`, 'success')
  }

  const handleImport = (imported: ImportedCard[], mode: 'append' | 'replace') => {
    /* The server matches on cr_name and answers with the refreshed table, so
       nothing is staged locally — an import is a write, not an edit. */
    router.post(
      '/game-config/cards/import',
      { cards: imported, mode } as never,
      {
        preserveScroll: true,
        onSuccess: () => setImportOpen(false),
        onError: () => toast('Import failed — nothing was changed.', 'danger'),
      },
    )
  }

  return (
    <GameConfigShell
      section="Cards"
      actions={
        <>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import CSV
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download />
            Export CSV
          </Button>
        </>
      }
      description="Balance tunables, straight off the cards table."
      dirtyCount={can_edit ? dirty.size : 0}
      onSave={can_edit ? handleSave : undefined}
      onRevert={can_edit ? handleRevert : undefined}
      saving={saving}
    >
      <ImportCardsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={handleImport}
      />

      <DataGrid
        columns={columns}
        rows={view.rows}
        total={view.total}
        state={state}
        onStateChange={setState}
        rowKey={(row) => row.id}
        onCellChange={handleCellChange}
        cellErrors={cellErrors}
        onAddRow={can_edit ? handleAddRow : undefined}
        addLabel="New card"
        noun="cards"
        paginate={false}
        maxHeight={560}
      />
    </GameConfigShell>
  )
}
